import { List, Map } from "immutable";
import { createSelector } from "reselect";

// returns a map of users { id: { _id, name, dataFields } }
export const getUsersMap = (state) => state.users.get("users");

export const getBillingAdmins = (state) => state.users.get("companyBillingAdmins");

export const getUserPermissionsMap = createSelector(getUsersMap, (users) =>
	users.map((user) => {
		const isAdmin = user.getIn(["adminFields", "admin"], false);
		const isBasicAdmin = user.getIn(["adminFields", "basicAdmin"], false);
		const isBillingAdmin = user.getIn(["adminFields", "billingAdmin"], false);

		if (isBillingAdmin) {
			return Map({ id: "billing-admins", label: "Billing Admin" });
		} else if (isAdmin) {
			return Map({ id: "admins", label: "Admin" });
		} else if (isBasicAdmin) {
			return Map({ id: "basic-admins", label: "Basic Admin" });
		}

		return Map({ id: "employees", label: "Employee" });
	})
);

export const getUserFieldsMap = createSelector(getUsersMap, (users) => {
	const fieldsMap = Map().withMutations((fieldsMutable) => {
		users.mapKeys((userId) => {
			/*
                    Not each user in this map has dataFields. This is because
                    this map gets filled in for user references like if a
                    user's manager. So for the case where 'dataFields' is
                    empty, the fieldsMap will only have an id and displayName
                */

			// get the fields map for the user
			const fields = users.getIn([userId, "dataFields"]);

			// add the user's id and display name to the fields
			const updatedFields = fields.withMutations((mutable) => {
				mutable.set("_id", Map({ value: users.getIn([userId, "_id"]), name: "id" }));
				mutable.set(
					"_displayName",
					Map({
						value: users.getIn([userId, "name"]),
						name: "Display Name",
					})
				);
			});

			fieldsMutable.set(userId, updatedFields);
		});
	});

	return fieldsMap;
});

export const getEmployeeName = (state, employeeID, notSetValue) => {
	const employees = getUsersMap(state);
	return employees.getIn([employeeID, "name"], notSetValue);
};

export const getEmployeeJobTitle = (state, employeeID, notSetValue = null) => {
	const employees = getUsersMap(state);
	return employees.getIn([employeeID, "jobTitle"], notSetValue);
};

export const getEmployeeEmail = (state, employeeID, notSetValue = null) => {
	const employees = getUsersMap(state);
	return employees.getIn([employeeID, "email"], notSetValue);
};

export const getEmployeeDirectReports = (state, employeeID, notSetValue = List()) => {
	const employees = getUsersMap(state);
	return employees.getIn([employeeID, "directReports"], notSetValue);
};

export const getEmployeeIsActive = (state, employeeID, notSetValue = null) => {
	const employees = getUsersMap(state);
	return employees.getIn([employeeID, "active"], notSetValue);
};
